import React, { useEffect, useCallback, useState, useRef } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { Sunrise, Anchor, Sprout, Circle, Compass, Lock } from "lucide-react";
import ARTSession from "./ARTSession";
import UnguidedSession from "./UnguidedSession";
import TourSession from "./TourSession";
import Footer from "./Footer";
import Header from "./Header";
import DynamicTitle from "./DynamicTitle";
import { useAuth } from "../contexts/AuthContext";
import { trackEvent } from "../utils/posthog";

const EMOTIONAL_OPTIONS = [
  { id: "overwhelmed", emoji: "😤", label: "Overwhelmed", descriptor: "Too much going on" },
  { id: "anxious",     emoji: "😰", label: "Anxious",     descriptor: "Mind won't slow down" },
  { id: "foggy",       emoji: "😶", label: "Foggy",       descriptor: "Disconnected, low energy" },
  { id: "heavy",       emoji: "😔", label: "Heavy",       descriptor: "Carrying something difficult" },
  { id: "okay",        emoji: "😐", label: "Okay",        descriptor: "Just maintaining" },
];

const TIME_OPTIONS = [
  { id: "~5",     label: "A few minutes",   sublabel: "(~5)" },
  { id: "~10",    label: "A short session", sublabel: "(~10)" },
  { id: "~15-20", label: "A full session",  sublabel: "(~15–20)" },
];

const RECOMMENDATION_MATRIX = {
  overwhelmed: { "~5": "safe-place",  "~10": "safe-place",       "~15-20": "grounding-release" },
  anxious:     { "~5": "safe-place",  "~10": "safe-place",       "~15-20": "grounding-release" },
  foggy:       { "~5": "daily-boost", "~10": "daily-boost",      "~15-20": "unguided" },
  heavy:       { "~5": "safe-place",  "~10": "grounding-release","~15-20": "grounding-release" },
  okay:        { "~5": "daily-boost", "~10": "daily-boost",      "~15-20": "unguided" },
};

const SESSION_META = {
  "safe-place": {
    slug: "safe-place",
    name: "Calm Space",
    description: "Build a peaceful place in your mind you can return to anytime.",
    duration: "about ~5–10 min",
    relativePath: "guided/safe-place",
    Icon: Anchor,
  },
  "grounding-release": {
    slug: "grounding-release",
    name: "Tension & Calm",
    description: "Notice tension in your body and let it soften as you follow the dot.",
    duration: "about ~10–15 min",
    relativePath: "guided/grounding-release",
    Icon: Sprout,
  },
  "daily-boost": {
    slug: "daily-boost",
    name: "Daily Boost",
    description: "Recall a happy memory and let that feeling grow as you follow the dot.",
    duration: "about ~5–10 min",
    relativePath: "guided/daily-boost",
    Icon: Sunrise,
  },
  "unguided": {
    slug: "unguided",
    name: "Unguided Session",
    description: "Self-directed — no script, no prompts. Follow your own pace and intuition.",
    duration: "self-paced",
    relativePath: "unguided",
    Icon: Circle,
  },
  "tour": {
    slug: "tour",
    name: "Tour",
    description: "A quick walk-through of how a session works.",
    duration: "~2 min",
    relativePath: "tour",
    Icon: Compass,
  },
};

const SESSION_LIST = ["safe-place", "grounding-release", "daily-boost", "unguided"];

function SessionSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSubscribed, subscriptionExpired, loading } = useAuth();

  const [step, setStep] = useState("emotional");
  const [emotionalState, setEmotionalState] = useState(null);
  const [timePreference, setTimePreference] = useState(null);
  const [showManualList, setShowManualList] = useState(false);

  const manualListRef = useRef(null);

  const isOnSessionRoute =
    location.pathname.includes("/sessions/guided/") ||
    location.pathname.includes("/sessions/unguided") ||
    location.pathname.includes("/sessions/tour");

  const validateToken = useCallback(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return false;
    }
    return true;
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    // Login is required; subscription is NOT — free users can run unguided
    // sessions. Guided sessions are locked individually below.
    validateToken();
  }, [validateToken, loading]);

  // Guided scripted sessions stay paid; unguided is free. Lapsed subscribers
  // (subscriptionExpired) are treated as free.
  const canUseGuided = isSubscribed && !subscriptionExpired;
  const isPremiumLocked = (slug) => slug !== "unguided" && !canUseGuided;

  const startUnguided = () => {
    trackEvent("script_selected", { script_id: "unguided", source: "just_get_started" });
    navigate(SESSION_META["unguided"].relativePath, { state: { intakeData: null } });
  };

  const recommendedSlug =
    emotionalState && timePreference
      ? RECOMMENDATION_MATRIX[emotionalState][timePreference]
      : null;
  const recommendedSession = recommendedSlug ? SESSION_META[recommendedSlug] : null;

  const handleEmotionalSelect = (stateId) => {
    setEmotionalState(stateId);
    setStep("time");
  };

  const handleTimeSelect = (timeId) => {
    setTimePreference(timeId);
    setStep("recommendation");
    const recSlug = RECOMMENDATION_MATRIX[emotionalState][timeId];
    trackEvent("session_recommendation_shown", {
      emotional_state: emotionalState,
      time_preference: timeId,
      recommended_session_id: recSlug,
    });
  };

  const buildIntakeData = (actualSlug) => ({
    emotionalState,
    timePreference,
    recommendedSlug,
    actualSlug,
  });

  const upsellToPremium = (slug) => {
    trackEvent("premium_session_upsell", { script_id: slug, source: "session_selection" });
    navigate("/subscribe", { state: { from: "/sessions" } });
  };

  const handleStartRecommended = () => {
    if (isPremiumLocked(recommendedSlug)) {
      upsellToPremium(recommendedSlug);
      return;
    }
    trackEvent("session_recommended", {
      emotional_state: emotionalState,
      time_preference: timePreference,
      recommended_session_id: recommendedSlug,
      accepted: true,
    });
    const session = SESSION_META[recommendedSlug];
    navigate(session.relativePath, { state: { intakeData: buildIntakeData(recommendedSlug) } });
  };

  const handleManualSelect = (slug) => {
    if (isPremiumLocked(slug)) {
      upsellToPremium(slug);
      return;
    }
    if (step === "recommendation") {
      trackEvent("session_recommended", {
        emotional_state: emotionalState,
        time_preference: timePreference,
        recommended_session_id: recommendedSlug,
        accepted: slug === recommendedSlug,
      });
    }
    trackEvent("script_selected", { script_id: slug });
    const intakeData = emotionalState ? buildIntakeData(slug) : null;
    navigate(SESSION_META[slug].relativePath, { state: { intakeData } });
  };

  const handleShowManualList = () => {
    setShowManualList(true);
    setTimeout(() => manualListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const showTourPrompt = user && !user.has_seen_tour && (user.session_count === 0);

  if (loading) {
    return (
      <div className="session-selection-page flex flex-col min-h-screen bg-background">
        <DynamicTitle>Session Select</DynamicTitle>
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <p className="text-textSecondary">Loading...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    // validateToken() redirects to /login; render nothing meaningful meanwhile.
    return null;
  }

  if (isOnSessionRoute) {
    return (
      <Routes>
        <Route path="guided/:scriptId" element={<ARTSession />} />
        <Route path="unguided" element={<UnguidedSession />} />
        <Route path="tour" element={<TourSession />} />
      </Routes>
    );
  }

  return (
    <div className="session-selection-page flex flex-col min-h-screen bg-background">
      <DynamicTitle>Session Select</DynamicTitle>
      <Header />

      <main className="flex-grow mx-auto w-full max-w-lg px-4 py-8 sm:py-12">

        {/* Tour opt-in prompt — new users only, one line */}
        {showTourPrompt && (
          <p className="text-center text-sm text-textSecondary mb-8">
            New here?{" "}
            <Link
              to="tour"
              className="text-accent underline-offset-2 hover:underline"
              onClick={() => trackEvent("tour_prompt_clicked")}
            >
              Take a quick tour →
            </Link>
          </p>
        )}

        {/* Step: emotional state */}
        {step === "emotional" && (
          <div>
            {/* Low-friction path: skip the intake, go straight to an unguided session. */}
            <button
              onClick={startUnguided}
              className="w-full bg-accent hover:bg-accentDark text-white font-semibold py-4 rounded-xl transition mb-4"
            >
              Just get started →
            </button>
            <div className="flex items-center gap-3 mb-8">
              <span className="flex-1 h-px bg-tint" />
              <span className="text-xs uppercase tracking-widest text-textSecondary">or tune it to you</span>
              <span className="flex-1 h-px bg-tint" />
            </div>
            <h1 className="text-2xl font-semibold text-text text-center mb-1">
              How are you feeling?
            </h1>
            <p className="text-textSecondary text-sm text-center mb-8">Pick whatever feels closest right now.</p>
            <div className="flex flex-col gap-3">
              {EMOTIONAL_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => handleEmotionalSelect(opt.id)}
                  className="w-full flex items-center gap-4 bg-white border border-tint rounded-xl px-5 py-4 text-left hover:border-tintHover transition-all active:scale-[0.98]"
                >
                  <span className="text-2xl leading-none">{opt.emoji}</span>
                  <span>
                    <span className="block font-medium text-text">{opt.label}</span>
                    <span className="block text-sm text-textSecondary">{opt.descriptor}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-10 text-center">
              <button
                onClick={() => setShowManualList(true)}
                className="text-sm text-textSecondary hover:text-text transition"
              >
                Skip — let me choose →
              </button>
            </div>

            {showManualList && (
              <ManualSessionList onSelect={handleManualSelect} listRef={null} isLocked={isPremiumLocked} />
            )}
          </div>
        )}

        {/* Step: time */}
        {step === "time" && (
          <div>
            <button
              onClick={() => setStep("emotional")}
              className="text-sm text-textSecondary hover:text-text mb-8 flex items-center gap-1 transition"
            >
              ← back
            </button>
            <h1 className="text-2xl font-semibold text-text text-center mb-1">
              How much time do you have?
            </h1>
            <p className="text-textSecondary text-sm text-center mb-8">We'll match you to something that fits.</p>
            <div className="flex flex-col gap-3">
              {TIME_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => handleTimeSelect(opt.id)}
                  className="w-full flex items-center justify-between bg-white border border-tint rounded-xl px-5 py-4 text-left hover:border-tintHover transition-all active:scale-[0.98]"
                >
                  <span className="font-medium text-text">{opt.label}</span>
                  <span className="text-textSecondary text-sm">{opt.sublabel}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step: recommendation */}
        {step === "recommendation" && recommendedSession && (
          <div>
            <div className="text-center mb-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                Recommended for you
              </span>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl px-6 py-7 shadow-sm mb-6">
              <div className="flex items-center gap-3 mb-2">
                {recommendedSession.Icon && (
                  <recommendedSession.Icon size={28} className="text-accent" strokeWidth={1.75} />
                )}
                <h2 className="text-xl font-semibold text-text">{recommendedSession.name}</h2>
                {isPremiumLocked(recommendedSlug) && <PremiumBadge />}
              </div>
              <p className="text-textSecondary text-sm mb-4">{recommendedSession.description}</p>
              <p className="text-xs text-textSecondary mb-6">{recommendedSession.duration}</p>
              <button
                onClick={handleStartRecommended}
                className="w-full bg-accent hover:bg-accentDark text-white font-semibold py-3 rounded-xl transition"
              >
                {isPremiumLocked(recommendedSlug) ? "Unlock with Premium" : "Start session"}
              </button>
            </div>

            <div className="text-center">
              <button
                onClick={handleShowManualList}
                className="text-sm text-textSecondary hover:text-text transition"
              >
                or choose a different session ↓
              </button>
            </div>

            {showManualList && (
              <ManualSessionList
                onSelect={handleManualSelect}
                listRef={manualListRef}
                highlightSlug={recommendedSlug}
                isLocked={isPremiumLocked}
              />
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function PremiumBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-accent bg-accent/10 rounded-full px-2 py-0.5">
      <Lock size={10} strokeWidth={2.5} />
      Premium
    </span>
  );
}

function ManualSessionList({ onSelect, listRef, highlightSlug, isLocked = () => false }) {
  return (
    <div ref={listRef} className="mt-10">
      <p className="text-xs font-semibold uppercase tracking-widest text-textSecondary mb-4 text-center">
        All sessions
      </p>
      <div className="flex flex-col gap-3">
        {SESSION_LIST.map((slug) => {
          const s = SESSION_META[slug];
          const isHighlighted = slug === highlightSlug;
          const locked = isLocked(slug);
          return (
            <button
              key={slug}
              onClick={() => onSelect(slug)}
              className={`w-full flex items-center gap-4 bg-white border rounded-xl px-5 py-4 text-left transition-all active:scale-[0.98] ${
                isHighlighted
                  ? "border-accent/40 opacity-70 hover:opacity-100 hover:border-accent"
                  : "border-gray-200 hover:border-tintHover"
              }`}
            >
              {s.Icon && (
                <s.Icon size={24} className="text-accent flex-shrink-0" strokeWidth={1.75} />
              )}
              <span className="flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text">{s.name}</span>
                  {locked && <PremiumBadge />}
                </span>
                <span className="block text-xs text-textSecondary mt-0.5">{s.duration}</span>
              </span>
              <span className="text-textSecondary text-sm">{locked ? "🔒" : "→"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default SessionSelection;
